import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaSave,
  FaTimes,
  FaCamera,
  FaIdBadge,
  FaCalendarAlt,
  FaCertificate,
  FaPhone,
  FaEnvelope,
  FaVenusMars,
  FaBirthdayCake,
  FaChalkboardTeacher,
  FaUserFriends,
  FaChartLine,
  FaCheck,
  FaMedal,
  FaFire,
  FaStar,
  FaRegStar,
  FaThumbsUp,
  FaRegThumbsUp,
  FaChevronDown,
  FaChevronUp,
  FaPlus,
  FaTrash,
  FaSync,
  FaWeight,
  FaRulerVertical,
  FaHeartbeat,
  FaUserMd,
  FaArrowLeft,
  FaHome,
  FaCity,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { RiMedalLine } from 'react-icons/ri';
import Avatar from '../../Components/Avatar';
import StarRating from '../../Components/StarRating';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatTrainerId, formatName } from '../../utils/dataTransformers';
import { useDateTimeSettings } from '../../hooks/useDateTimeSettings';
import { formatDate as formatDateUtil } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';
import UpgradeModal from '../../Components/UpgradeModal';
import { AVAILABLE_SERVICES } from '../../utils/serviceConstants';

function TrainerProfile() {
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState({
    _id: '',
    name: '',
    email: '',
    mobile: '',
    gender: '',
    service: '',
    status: 'pending',
    dateOfBirth: null,
    yearsOfExperience: 0,
    certification: '',
    bio: '',
    teachingStyle: '',
    languages: [],
    achievements: [],
    serviceChangeRequest: null,
    requestStatus: null,
    createdAt: new Date(),
    updatedAt: new Date(),
    programs: [],
    unreadMessages: 0,
    rating: 0, // Auto-calculated default rating from backend
    reviews: 0, // Total review count from backend
    isDefaultRating: false, // Flag indicating if rating is auto-calculated
    height: '', // Height in cm
    weight: '', // Weight in kg
    bloodGroup: '', // Blood group
    emergencyContact: { // Emergency contact {name, phone, relationship}
      name: '',
      phone: '',
      relationship: ''
    },
    address: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
  });
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showSpecializationModal, setShowSpecializationModal] = useState(false);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [adminApproved, setAdminApproved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('multi-service');

  const [reviews, setReviews] = useState([]);

  // Calculate age from dateOfBirth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    activePrograms: 0,
    monthlyRevenue: 0,
    completionRate: 0,
    upcomingClasses: 0,
    sessionsTaught: 0,
    streakDays: 0
  });

  // Fetch dashboard stats from API
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const response = await HttpInterceptor.get('/api/trainer/dashboard/stats');
        if (response.status === 200) {
          const statsData = response.data?.data || response.data || {};
          setDashboardStats({
            totalStudents: statsData.totalStudents || 0,
            activePrograms: statsData.activePrograms || 0,
            monthlyRevenue: statsData.monthlyRevenue || 0,
            completionRate: statsData.completionRate || 0,
            upcomingClasses: statsData.upcomingClasses || 0,
            sessionsTaught: statsData.sessionsTaught || 0,
            streakDays: statsData.streakDays || 0
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        // Use fallback values from trainer data if API fails
      }
    };
    fetchDashboardStats();
  }, []);

  const calculateStats = useCallback((trainerData) => {
    // Use API stats if available, otherwise calculate from trainer data (no hardcoded multipliers)
    const totalStudents = dashboardStats.totalStudents || 0;
    const activePrograms = dashboardStats.activePrograms || (trainerData.programs?.filter((program) => program.isActive).length || 0);
    // Don't use hardcoded multiplier - show 0 if no revenue data available
    const monthlyRevenue = dashboardStats.monthlyRevenue || 0;
    // Don't use hardcoded percentage - show 0 if no completion data available
    const completionRate = dashboardStats.completionRate || 0;
    const upcomingClasses = dashboardStats.upcomingClasses || (trainerData.programs?.reduce(
      (sum, program) => sum + (program.sessions?.filter((s) => !s.completed).length || 0),
      0
    ) || 0);
    const sessionsTaught = dashboardStats.sessionsTaught || (trainerData.programs?.reduce(
      (sum, program) => sum + (program.sessions?.filter((s) => s.completed).length || 0),
      0
    ) || 0);

    return {
      totalStudents,
      activePrograms,
      monthlyRevenue,
      completionRate,
      upcomingClasses,
      sessionsTaught,
      streakDays: dashboardStats.streakDays || 0,
      // Use rating from API which includes auto-calculated default rating when no reviews
      // If reviews exist, calculate average from reviews (more accurate than API rating which might be cached)
      rating: reviews.length > 0
        ? Math.round((reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length) * 10) / 10
        : (trainer.rating || 0), // Use auto-calculated default rating from API when no reviews
    };
  }, [reviews, dashboardStats]);

  // Memoize stats calculation
  const stats = useMemo(() => calculateStats(trainer), [trainer, calculateStats]);

  const [showReviewDetails, setShowReviewDetails] = useState(false);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const { data } = await HttpInterceptor.get('/api/trainer/profile');

      // Handle different response formats
      const profileData = data?.data || data || {};

      // Validate and transform profile data with safe defaults
      const trainerData = {
        ...profileData,
        name: profileData.name || '',
        email: profileData.email || '',
        mobile: profileData.mobile || profileData.phone || '',
        gender: profileData.gender || 'other',
        service: profileData.service || profileData.specialization || '',
        bio: (profileData.bio && profileData.bio !== 'Not specified' && profileData.bio !== 'Not Specified') ? profileData.bio : '',
        experience: profileData.experience || profileData.yearsOfExperience || 0,
        certification: (profileData.certification && profileData.certification !== 'Not specified' && profileData.certification !== 'Not Specified') ? profileData.certification : '',
        teachingStyle: (profileData.teachingStyle && profileData.teachingStyle !== 'Not specified' && profileData.teachingStyle !== 'Not Specified') ? profileData.teachingStyle : '',
        languages: Array.isArray(profileData.languages) ? profileData.languages : [],
        achievements: Array.isArray(profileData.achievements) ? profileData.achievements : [],
        programs: Array.isArray(profileData.programs) ? profileData.programs : [],
        rating: profileData.rating || 0, // Auto-calculated default rating from backend when no reviews
        reviews: profileData.reviews || 0, // Total review count from backend
        isDefaultRating: profileData.isDefaultRating || false, // Flag indicating if rating is auto-calculated
        // Normalize empty values - remove "Not specified" strings
        height: (profileData.height && profileData.height !== 'Not specified' && profileData.height !== 'Not Specified') ? profileData.height : '',
        weight: (profileData.weight && profileData.weight !== 'Not specified' && profileData.weight !== 'Not Specified') ? profileData.weight : '',
        bloodGroup: (profileData.bloodGroup && profileData.bloodGroup !== 'Not specified' && profileData.bloodGroup !== 'Not Specified') ? profileData.bloodGroup : '',
        emergencyContact: (profileData.emergencyContact && typeof profileData.emergencyContact === 'object')
          ? {
            name: (profileData.emergencyContact.name && profileData.emergencyContact.name !== 'Not specified' && profileData.emergencyContact.name !== 'Not Specified') ? profileData.emergencyContact.name : '',
            phone: (profileData.emergencyContact.phone && profileData.emergencyContact.phone !== 'Not specified' && profileData.emergencyContact.phone !== 'Not Specified') ? profileData.emergencyContact.phone : '',
            relationship: (profileData.emergencyContact.relationship && profileData.emergencyContact.relationship !== 'Not specified' && profileData.emergencyContact.relationship !== 'Not Specified') ? profileData.emergencyContact.relationship : ''
          }
          : {
            name: '',
            phone: '',
            relationship: ''
          },
        address: (profileData.address && typeof profileData.address === 'object')
          ? {
            addressLine1: profileData.address.addressLine1 || '',
            addressLine2: profileData.address.addressLine2 || '',
            city: profileData.address.city || '',
            state: profileData.address.state || '',
            pincode: profileData.address.pincode || '',
            country: profileData.address.country || 'India'
          }
          : {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            pincode: '',
            country: 'India'
          },
        totalStudents: profileData.totalStudents || 0,
        totalClasses: profileData.totalClasses || 0,
        status: profileData.status || 'active',
        requestStatus: profileData.requestStatus || null,
        serviceChangeRequest: profileData.serviceChangeRequest || null,
      };

      setTrainer(trainerData);

      // Fetch reviews if available in profile data, otherwise fetch separately
      if (profileData.reviews && Array.isArray(profileData.reviews)) {
        setReviews(profileData.reviews);
      } else {
        // Fetch reviews separately from the reviews endpoint
        try {
          const reviewsResponse = await HttpInterceptor.get('/api/trainer/reviews');
          if (reviewsResponse.status === 200) {
            const responseData = reviewsResponse.data?.data || reviewsResponse.data || {};
            const reviewsData = responseData.reviews || [];
            setReviews(Array.isArray(reviewsData) ? reviewsData : []);
          }
        } catch (reviewsError) {
          // Reviews endpoint may not exist, use empty array
          console.log('Reviews not available:', reviewsError);
          setReviews([]);
        }
      }
    } catch (error) {
      console.error('Failed to load profile:', error);

      // Handle different error types
      let errorMsg = 'Failed to load profile';
      if (error.type === 'network') {
        errorMsg = 'Network error. Please check your connection.';
      } else if (error.status === 401) {
        errorMsg = 'Session expired. Please log in again.';
      } else if (error.status === 403) {
        errorMsg = 'You don\'t have permission to view this profile.';
      } else if (error.status >= 500) {
        errorMsg = 'We’re having trouble right now. Please try again in a moment.';
      } else if (error.message) {
        errorMsg = error.message;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }

      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (error.type !== 'network' && error.status !== 401) {
        toast.error(errorMsg);
      }

      // Set default trainer data on error to prevent crashes
      setTrainer({
        name: '',
        email: '',
        mobile: '',
        gender: 'other',
        service: '',
        bio: '',
        experience: 0,
        certification: '',
        teachingStyle: '',
        languages: [],
        achievements: [],
        programs: [],
        rating: 0,
        totalStudents: 0,
        totalClasses: 0,
        status: 'active',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Note: Removed simulation code - specialization changes should be handled by admin approval via API
  // This useEffect was simulating admin approval/rejection which should not be in production code
  // The actual approval/rejection should be handled through the admin dashboard

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested properties like 'emergencyContact.name'
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setTrainer((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setTrainer((prev) => ({ ...prev, [name]: value }));
    }
    setHasChanges(true);

    // Auto-fetch city and state from pincode
    if (name === 'address.pincode' && value.length === 6 && /^[0-9]{6}$/.test(value)) {
      fetchCityStateFromPincode(value);
    }
  };

  const fetchCityStateFromPincode = async (pincode) => {
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      if (data && data[0] && data[0].Status === 'Success') {
        const { District, State, Country } = data[0].PostOffice[0];
        setTrainer(prev => ({
          ...prev,
          address: {
            ...prev.address,
            city: District,
            state: State,
            country: Country || 'India'
          }
        }));
      } else {
        toast.error('Invalid Pincode');
      }
    } catch (error) {
      console.error('Error fetching pincode details:', error);
    }
  };


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
        setProfileImageFile(file);
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleListChange = (name, index, value) => {
    setTrainer((prev) => {
      const updatedList = [...prev[name]];
      updatedList[index] = value;
      return { ...prev, [name]: updatedList };
    });
    setHasChanges(true);
  };

  const handleAddListItem = (name) => {
    setTrainer((prev) => ({
      ...prev,
      [name]: [...prev[name], ''],
    }));
    setHasChanges(true);
  };

  const handleRemoveListItem = (name, index) => {
    setTrainer((prev) => ({
      ...prev,
      [name]: prev[name].filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  };

  const validateProfileData = (data) => {
    const errors = [];

    // Validate name
    if (!data.name || !data.name.trim()) {
      errors.push('Name is required');
    } else if (data.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters');
    } else if (data.name.trim().length > 100) {
      errors.push('Name must be at most 100 characters');
    }

    // Validate mobile
    if (data.mobile) {
      const cleanedMobile = data.mobile.replace(/[\s-]/g, '');
      if (cleanedMobile.length !== 10) {
        errors.push('Mobile number must be 10 digits');
      } else if (!/^[6-9]\d{9}$/.test(cleanedMobile)) {
        errors.push('Mobile number must start with 6, 7, 8, or 9');
      }
    }

    // Validate gender
    if (data.gender && !['male', 'female', 'other'].includes(data.gender)) {
      errors.push('Invalid gender selection');
    }

    // Validate years of experience
    if (data.yearsOfExperience !== undefined && data.yearsOfExperience !== null) {
      const exp = Number(data.yearsOfExperience);
      if (isNaN(exp) || exp < 0 || exp > 50) {
        errors.push('Years of experience must be between 0 and 50');
      }
    }

    // Validate bio length
    if (data.bio && data.bio.length > 500) {
      errors.push('Bio must be at most 500 characters');
    }

    // Validate teaching style length
    if (data.teachingStyle && data.teachingStyle.length > 200) {
      errors.push('Teaching style must be at most 200 characters');
    }

    // Validate address
    if (data.address) {
      if (data.address.addressLine1 && !data.address.addressLine1.trim()) {
        errors.push('Address Line 1 is required');
      }
      if (data.address.city && !data.address.city.trim()) {
        errors.push('City is required');
      }
      if (data.address.state && !data.address.state.trim()) {
        errors.push('State is required');
      }
      if (data.address.pincode) {
        if (!/^[0-9]{6}$/.test(data.address.pincode)) {
          errors.push('Enter a valid 6-digit pincode');
        }
      } else if (data.address.pincode !== undefined) {
        errors.push('Pincode is required');
      }
    }

    return errors;
  };

  const handleSaveChanges = async () => {
    try {
      const updateData = {
        name: trainer.name,
        mobile: trainer.mobile,
        gender: trainer.gender,
        dateOfBirth: trainer.dateOfBirth,
        yearsOfExperience: trainer.yearsOfExperience,
        certification: trainer.certification,
        bio: trainer.bio,
        teachingStyle: trainer.teachingStyle,
        languages: trainer.languages,
        achievements: trainer.achievements,
        height: trainer.height,
        weight: trainer.weight,
        bloodGroup: trainer.bloodGroup,
        emergencyContact: trainer.emergencyContact,
        address: trainer.address,
      };

      // Validate before submitting
      const validationErrors = validateProfileData(updateData);
      if (validationErrors.length > 0) {
        toast.error(validationErrors[0]);
        return;
      }

      // Upload profile image first (if selected)
      if (profileImageFile) {
        const formData = new FormData();
        formData.append('profileImage', profileImageFile);

        const uploadRes = await HttpInterceptor.post('/api/trainer/profile/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const uploadedUrl = uploadRes?.data?.profileImage;
        if (uploadedUrl) {
          const cacheBustedUrl = `${uploadedUrl}${uploadedUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
          setTrainer((prev) => ({ ...prev, profileImage: cacheBustedUrl }));
        }

        setProfileImagePreview(null);
        setProfileImageFile(null);
      }

      const { data } = await HttpInterceptor.put('/api/trainer/profile', updateData);
      setTrainer({
        ...data.data,
        mobile: data.data.mobile || '',
        programs: data.data.programs || [],
        languages: data.data.languages || [],
        achievements: data.data.achievements || [],
        height: data.data.height || '',
        weight: data.data.weight || '',
        bloodGroup: data.data.bloodGroup || '',
        emergencyContact: data.data.emergencyContact || {
          name: '',
          phone: '',
          relationship: ''
        },
        address: data.data.address || {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          pincode: '',
          country: 'India'
        },
        // Explicitly preserve rating fields from update response
        rating: data.data.rating || 0,
        reviews: data.data.reviews || 0,
        isDefaultRating: data.data.isDefaultRating || false,
      });
      setHasChanges(false);
      setEditMode(false);
      setAdminApproved(false);
      setProfileImagePreview(null);
      setProfileImageFile(null);

      // Refresh reviews to show updated profile images
      try {
        const reviewsResponse = await HttpInterceptor.get('/api/trainer/reviews');
        if (reviewsResponse.status === 200) {
          const responseData = reviewsResponse.data?.data || reviewsResponse.data || {};
          const reviewsData = responseData.reviews || [];
          setReviews(Array.isArray(reviewsData) ? reviewsData : []);
        }
      } catch (reviewsError) {
        console.log('Could not refresh reviews:', reviewsError);
      }

      toast.success('Profile updated successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMsg);
      console.error('Profile update error:', error);
    }
  };

  const handleCancelChanges = () => {
    setTrainer((prev) => ({ ...prev }));
    setProfileImagePreview(null);
    setProfileImageFile(null);
    setHasChanges(false);
    setEditMode(false);
    setAdminApproved(false);
  };

  const handleSpecializationChangeRequest = () => {
    // Check if trainer already has a service - show upgrade prompt for multi-service feature
    // Since multi-service (adding second service) isn't fully implemented yet,
    // show upgrade prompt as informational placeholder for future multi-service feature
    if (trainer.service && trainer.service.trim() !== '') {
      // Show upgrade prompt for multi-service feature
      setUpgradeReason('multi-service');
      setShowUpgradeModal(true);
      // Note: When multi-service is fully implemented, this will check if user is trying
      // to add a second service (not replace) and show upgrade prompt accordingly
      return;
    }
    // If no existing service, proceed directly with specialization modal
    setShowSpecializationModal(true);
    setNewSpecialization(trainer.service);
  };


  const handleSpecializationSubmit = async () => {
    try {
      if (!AVAILABLE_SERVICES.includes(newSpecialization)) {
        toast.error('Invalid specialization selected');
        return;
      }
      const { data, status } = await HttpInterceptor.post('/api/trainer/specialization/request', { newService: newSpecialization });
      if (status === 200) {
        setTrainer((prev) => ({
          ...prev,
          serviceChangeRequest: newSpecialization,
          requestStatus: 'pending',
        }));
        setShowSpecializationModal(false);
        toast.info('Specialization change request submitted');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to submit specialization change request';
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.status === 400 && errorMessage.includes('pending')) {
        toast.error('A specialization change request is already pending');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleRefreshProfile = async () => {
    await fetchProfile();
    toast.info('Profile data refreshed');
  };

  const getRequestStatusText = () => {
    if (trainer.requestStatus === 'pending') return '(Request Submitted)';
    if (trainer.requestStatus === 'approved') return '(Change Approved)';
    if (trainer.requestStatus === 'rejected') return '(Request Rejected)';
    return '(Request Change)';
  };

  const handleLikeReview = (reviewId) => {
    setReviews((prev) =>
      prev.map((review) => {
        if (review.id === reviewId) {
          const isLiked = !review.liked;
          return {
            ...review,
            liked: isLiked,
            likes: isLiked ? review.likes + 1 : review.likes - 1,
          };
        }
        return review;
      })
    );
  };

  const dateTimeSettings = useDateTimeSettings();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDateUtil(dateString, dateTimeSettings.dateFormat);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  const renderStars = (rating, interactive = false, size = 'md') => {
    // For interactive stars (in review modals), keep the original implementation
    if (interactive) {
      const sizes = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
        xl: 'h-8 w-8',
      };
      return (
        <div className="flex" role="img" aria-label={`Rating: ${rating} out of 5 stars`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => { }}
              className="focus:outline-none"
              aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            >
              <FaStar
                className={`${sizes[size]} ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      );
    }
    // For display-only stars, use StarRating component
    return <StarRating rating={rating || 0} size={size} />;
  };

  const StatCard = ({ title, value, icon, trend, color, bgColor, progress }) => (
    <div className={`p-4 rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md ${bgColor}`}>
      <div className="flex justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${bgColor} ${color}`}>{icon}</div>
      </div>
      <div className="mt-2">
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${color.replace('text', 'bg')}`} style={{ width: `${progress}%` }}></div>
        </div>
        <p className="text-xs text-gray-500 mt-1">{trend}</p>
      </div>
    </div>
  );

  // Helper function to normalize value for edit mode (remove "Not specified" and empty strings)
  const normalizeValueForEdit = (value) => {
    if (!value || value === 'Not specified' || value === 'Not Specified' || value === 'not specified') {
      return '';
    }
    return value;
  };

  // Helper function to get placeholder text based on field type
  const getPlaceholder = (label, type) => {
    if (type === 'date') return 'Select date';
    if (type === 'email') return 'Enter email address';
    if (type === 'tel') return 'Enter 10-digit phone number';
    if (type === 'number') return `Enter ${label.toLowerCase()}`;
    // Specific placeholders for better guidance
    if (label.toLowerCase().includes('bio')) return 'Write a brief description about yourself, your teaching philosophy, and what students can expect...';
    if (label.toLowerCase().includes('contact name')) return 'Enter emergency contact full name';
    if (label.toLowerCase().includes('contact phone')) return 'Enter 10-digit emergency contact number';
    if (label.toLowerCase().includes('teaching style')) return 'e.g., Power yoga, Hatha yoga, Vinyasa flow...';
    if (label.toLowerCase().includes('certification')) return 'e.g., RYT-200, Certified Yoga Instructor...';
    return `Enter ${label.toLowerCase()}`;
  };

  const renderField = (label, name, value, icon, type = 'text', editable = true, options = null) => {
    // Normalize value: use raw value in edit mode, formatted value in view mode
    const displayValue = editMode && editable ? normalizeValueForEdit(value) : value;
    const placeholder = editMode && editable ? getPlaceholder(label, type) : '';

    return (
      <div className="mb-4">
        <label className="flex items-center text-gray-600 mb-2">
          <span className="mr-2 text-green-600">{icon}</span>
          {label}
        </label>
        {editMode && editable ? (
          type === 'select' || options ? (
            <select
              name={name}
              value={displayValue || ''}
              onChange={handleInputChange}
              className="w-full p-2 border-b-2 border-green-300 focus:border-green-500 focus:outline-none"
            >
              <option value="">Select {label.toLowerCase()}</option>
              {options ? options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              )) : (
                <>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="others">Others</option>
                </>
              )}
            </select>
          ) : type === 'date' ? (
            <input
              type="date"
              name={name}
              value={displayValue ? new Date(displayValue).toISOString().split('T')[0] : ''}
              onChange={handleInputChange}
              className="w-full p-2 border-b-2 border-green-300 focus:border-green-500 focus:outline-none"
            />
          ) : (
            <input
              type={type}
              name={name}
              value={displayValue || ''}
              onChange={handleInputChange}
              placeholder={placeholder}
              className="w-full p-2 border-b-2 border-green-300 focus:border-green-500 focus:outline-none placeholder-gray-400"
            />
          )
        ) : (
          <div className="text-lg font-medium text-gray-800 pl-6">
            {type === 'date' && value ? `${calculateAge(value)} years` : value || 'Not specified'}
          </div>
        )}
      </div>
    );
  };

  const renderTextArea = (label, name, value, icon) => {
    const displayValue = editMode ? normalizeValueForEdit(value) : value;
    const placeholder = editMode ? getPlaceholder(label, 'text') : '';

    return (
      <div className="mb-6">
        <label className="flex items-center text-gray-600 mb-2">
          <span className="mr-2 text-green-600">{icon}</span>
          {label}
        </label>
        {editMode ? (
          <div>
            <textarea
              name={name}
              value={displayValue || ''}
              onChange={handleInputChange}
              rows="5"
              placeholder={placeholder}
              className="w-full p-3 border-2 border-green-300 rounded-lg focus:border-green-500 focus:outline-none placeholder-gray-400"
              maxLength={500}
            />
            {label.toLowerCase().includes('bio') && (
              <p className="text-xs text-gray-500 mt-1">
                {displayValue?.length || 0}/500 characters
              </p>
            )}
          </div>
        ) : (
          <div className="text-gray-700 pl-6 border-l-2 border-green-200">{value || 'No bio provided'}</div>
        )}
      </div>
    );
  };

  const renderList = (label, name, items, icon) => (
    <div className="mb-6">
      <label className="flex items-center text-gray-600 mb-2">
        <span className="mr-2 text-green-600">{icon}</span>
        {label}
      </label>
      {editMode ? (
        <div className="pl-6 space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => handleListChange(name, index, e.target.value)}
                className="w-full p-2 border-b-2 border-green-300 focus:border-green-500 focus:outline-none"
                placeholder={
                  label.toLowerCase().includes('language')
                    ? 'e.g., English, Hindi, Spanish...'
                    : label.toLowerCase().includes('achievement')
                      ? 'e.g., 10+ years experience, 500+ students trained, Yoga Alliance Certified...'
                      : `Enter ${label.toLowerCase().slice(0, -1)}`
                }
              />
              <button
                onClick={() => handleRemoveListItem(name, index)}
                className="text-red-600 hover:text-red-800"
                title="Remove"
              >
                <FaTrash />
              </button>
            </div>
          ))}
          <button
            onClick={() => handleAddListItem(name)}
            className="flex items-center gap-2 text-green-600 hover:text-green-800 mt-2"
          >
            <FaPlus /> Add {label.slice(0, -1)}
          </button>
        </div>
      ) : (
        <div className="pl-6">
          {items && items.length > 0 ? (
            <ul className="space-y-2">
              {items.map((item, index) => (
                <li key={index} className="flex items-start">
                  <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-gray-500">No {label.toLowerCase()} specified</div>
          )}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/trainer/home')}
          className="flex items-center text-gray-600 hover:text-green-600 mb-4 transition-colors"
          aria-label="Go back to dashboard"
        >
          <FaArrowLeft className="mr-2" aria-hidden="true" />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-800 px-8 py-6 text-white relative">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold">My Profile</h1>
                <p className="text-green-100">Manage your professional information</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                  <div
                    className={`w-3 h-3 rounded-full ${trainer.status === 'active' ? 'bg-green-600' : 'bg-red-600'}`}
                  ></div>
                  <span className="text-sm text-black">
                    {trainer.status.charAt(0).toUpperCase() + trainer.status.slice(1)}
                  </span>
                </div>
                {!editMode ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditMode(true)}
                      className="flex items-center gap-2 bg-white text-green-700 px-4 py-2 rounded-full hover:bg-green-50 transition-colors"
                    >
                      <FaSave /> Edit Profile
                    </button>
                    <button
                      onClick={handleRefreshProfile}
                      className="flex items-center gap-2 bg-white text-blue-700 px-4 py-2 rounded-full hover:bg-blue-50 transition-colors"
                      title="Refresh Profile Data"
                    >
                      <FaSync /> Refresh
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveChanges}
                      disabled={!hasChanges}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full ${hasChanges
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      <FaSave /> Save Changes
                    </button>
                    <button
                      onClick={handleCancelChanges}
                      className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-300 transition-colors"
                    >
                      <FaTimes /> Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="p-6 md:flex gap-8">
            <div className="md:w-1/3 mb-8 md:mb-0">
              <div className="flex flex-col items-center sticky top-6">
                <div className="relative mb-6">
                  <div className="w-48 h-48 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md relative">
                    {editMode && profileImagePreview ? (
                      <img src={profileImagePreview} alt="Trainer preview" className="w-full h-full object-cover" />
                    ) : (
                      <Avatar
                        src={trainer?.profileImage}
                        name={trainer?.name || 'Trainer'}
                        size={192}
                        alt="Trainer profile"
                      />
                    )}
                  </div>
                  {editMode && (
                    <label className="absolute -bottom-2 right-2 bg-green-600 text-white p-3 rounded-full cursor-pointer hover:bg-green-700 shadow-lg transition-all flex items-center justify-center z-10">
                      <FaCamera size={16} />
                      <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                    </label>
                  )}
                </div>
                <div className="text-center w-full">
                  <h2 className="text-2xl font-bold text-gray-800 mb-1">{formatName(trainer.name)}</h2>
                  <div className="flex items-center justify-center">
                    <p className="text-green-600 font-medium mb-4 mr-2">{trainer.service}</p>
                    {editMode && (
                      <button
                        onClick={handleSpecializationChangeRequest}
                        className={`text-xs ${trainer.requestStatus === 'pending'
                          ? 'text-yellow-600'
                          : trainer.requestStatus === 'approved'
                            ? 'text-green-600'
                            : trainer.requestStatus === 'rejected'
                              ? 'text-red-600'
                              : 'text-green-700 hover:text-green-900'
                          }`}
                        title={
                          trainer.requestStatus === 'pending'
                            ? 'Request pending admin approval'
                            : trainer.requestStatus === 'approved'
                              ? 'Change approved'
                              : trainer.requestStatus === 'rejected'
                                ? 'Request rejected'
                                : 'Request specialization change'
                        }
                        disabled={trainer.requestStatus === 'pending'}
                      >
                        {getRequestStatusText()}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <StarRating
                      rating={stats.rating || 0}
                      size="md"
                      showNumber={true}
                      showReviews={true}
                      reviews={reviews.length > 0 ? reviews.length : (trainer.reviews || 0)}
                    />
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg w-full mb-4">
                    <div className="flex items-center justify-center text-gray-500 text-sm">
                      <FaCalendarAlt className="mr-2 text-green-600" />
                      <span>Member since {formatDate(trainer.createdAt)}</span>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg w-full mb-6">
                    <div className="flex items-center justify-center">
                      <FaIdBadge className="text-green-600 mr-3 text-xl" />
                      <div>
                        <div className="text-xs text-gray-500">Trainer ID</div>
                        <div className="font-bold text-green-800">{formatTrainerId(trainer._id, trainer.name)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center justify-center gap-3">
                        <FaChalkboardTeacher className="text-blue-600 text-xl" />
                        <div>
                          <div className="text-xs text-gray-500">Sessions Taught</div>
                          <div className="font-bold text-blue-800">{stats.sessionsTaught}</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center justify-center gap-3">
                        <FaFire className="text-yellow-600 text-xl" />
                        <div>
                          <div className="text-xs text-gray-500">Current Streak</div>
                          <div className="font-bold text-yellow-800">{stats.streakDays} days</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center justify-center gap-3">
                        <RiMedalLine className="text-purple-600 text-xl" />
                        <div>
                          <div className="text-xs text-gray-500">Rating</div>
                          <div className="font-bold text-purple-800">{stats.rating.toFixed(1)}/5.0</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-2/3 bg-white rounded-lg p-6 shadow-sm">
              <div className="space-y-8">
                <section>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderField('Full Name', 'name', editMode ? trainer.name : formatName(trainer.name), <FaUser />)}
                    {renderField('Date of Birth', 'dateOfBirth', trainer.dateOfBirth, <FaBirthdayCake />, 'date')}
                    {renderField('Gender', 'gender', trainer.gender, <FaVenusMars />, 'select')}
                    {renderField('Email', 'email', trainer.email, <FaEnvelope />, 'email')}
                    {renderField('Phone', 'mobile', trainer.mobile, <FaPhone />, 'tel')}
                    {renderField('Height (cm)', 'height', trainer.height, <FaRulerVertical />)}
                    {renderField('Weight (kg)', 'weight', trainer.weight, <FaWeight />)}
                    {renderField('Blood Group', 'bloodGroup', trainer.bloodGroup, <FaHeartbeat />, 'select', true, [
                      { value: 'A+', label: 'A+' },
                      { value: 'A-', label: 'A-' },
                      { value: 'B+', label: 'B+' },
                      { value: 'B-', label: 'B-' },
                      { value: 'AB+', label: 'AB+' },
                      { value: 'AB-', label: 'AB-' },
                      { value: 'O+', label: 'O+' },
                      { value: 'O-', label: 'O-' }
                    ])}
                  </div>
                  {/* Emergency Contact Section */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FaUserMd className="mr-2 text-green-600" />
                      Emergency Contact
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {renderField('Contact Name', 'emergencyContact.name', editMode ? (trainer.emergencyContact?.name || '') : formatName(trainer.emergencyContact?.name || ''), <FaUser />)}
                      {renderField('Contact Phone', 'emergencyContact.phone', trainer.emergencyContact?.phone || '', <FaPhone />, 'tel')}
                      {renderField('Relationship', 'emergencyContact.relationship', trainer.emergencyContact?.relationship || '', <FaUser />, 'select', true, [
                        { value: 'Spouse', label: 'Spouse' },
                        { value: 'Parent', label: 'Parent' },
                        { value: 'Sibling', label: 'Sibling' },
                        { value: 'Child', label: 'Child' },
                        { value: 'Friend', label: 'Friend' },
                        { value: 'Other', label: 'Other' }
                      ])}
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FaHome className="mr-2 text-green-600" />
                      Address Details
                    </h4>
                    {renderField('Address Line 1', 'address.addressLine1', trainer.address?.addressLine1, <FaHome />)}
                    {renderField('Address Line 2', 'address.addressLine2', trainer.address?.addressLine2, <FaHome />)}
                    {renderField('Pincode', 'address.pincode', trainer.address?.pincode, <FaMapMarkerAlt />, 'text', true)}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderField('City', 'address.city', trainer.address?.city, <FaCity />)}
                      {renderField('State', 'address.state', trainer.address?.state, <FaMapMarkerAlt />)}
                    </div>
                  </div>
                </section>
                <section>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Professional Information</h3>
                  {renderField('Specialization', 'service', trainer.service, <FaCertificate />, 'text', false)}
                  {renderField('Years of Experience', 'yearsOfExperience', trainer.yearsOfExperience, <FaCertificate />, 'number')}
                  {renderField('Certification', 'certification', trainer.certification, <FaCertificate />)}
                  {renderField('Teaching Style', 'teachingStyle', trainer.teachingStyle, <FaChalkboardTeacher />)}
                  {renderTextArea('Bio', 'bio', trainer.bio, <FaCertificate />)}
                  {renderList('Languages', 'languages', trainer.languages, <FaCheck />)}
                  {renderList('Achievements', 'achievements', trainer.achievements, <FaMedal />)}
                </section>
                <section>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Training Statistics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <StatCard
                      title="Total Students"
                      value={stats.totalStudents}
                      icon={<FaUserFriends className="text-purple-500" />}
                      trend={stats.totalStudents > 0 ? `${stats.totalStudents} ${stats.totalStudents === 1 ? 'student' : 'students'}` : "No data available"}
                      color="text-purple-600"
                      bgColor="bg-purple-50"
                      progress={0} // Progress should be based on actual goals, not hardcoded divisor
                    />
                    <StatCard
                      title="Active Programs"
                      value={stats.activePrograms}
                      icon={<FaCertificate className="text-green-500" />}
                      trend={stats.activePrograms > 0 ? `${stats.activePrograms} ${stats.activePrograms === 1 ? 'program' : 'programs'}` : "No data available"}
                      color="text-green-600"
                      bgColor="bg-green-50"
                      progress={0} // Progress should be based on actual goals, not hardcoded divisor
                    />
                    <StatCard
                      title="Monthly Revenue"
                      value={formatCurrency(stats.monthlyRevenue)}
                      icon={<FaCertificate className="text-yellow-500" />}
                      trend={stats.monthlyRevenue > 0 ? `${formatCurrency(stats.monthlyRevenue)} this month` : "No data available"}
                      color="text-yellow-600"
                      bgColor="bg-yellow-50"
                      progress={0} // Progress should be calculated based on actual revenue goals, not hardcoded
                    />
                    <StatCard
                      title="Completion Rate"
                      value={`${stats.completionRate}%`}
                      icon={<FaChartLine className="text-blue-500" />}
                      trend={stats.completionRate > 0 ? `${stats.completionRate}% average` : "No data available"}
                      color="text-blue-600"
                      bgColor="bg-blue-50"
                      progress={stats.completionRate}
                    />
                  </div>
                </section>
                <section>
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="text-xl font-semibold text-gray-800">Student Reviews</h3>
                    <button
                      onClick={() => setShowReviewDetails(!showReviewDetails)}
                      className="text-green-600 hover:text-green-800 flex items-center text-sm"
                    >
                      {showReviewDetails ? 'Hide Reviews' : 'Show Reviews'}
                      {showReviewDetails ? <FaChevronUp className="ml-1" /> : <FaChevronDown className="ml-1" />}
                    </button>
                  </div>
                  {showReviewDetails && (
                    <div className="space-y-4">
                      {reviews.length > 0 ? (
                        reviews.map((review) => (
                          <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center space-x-3">
                                <Avatar
                                  src={review.userImage}
                                  name={review.userName || 'User'}
                                  size={40}
                                  alt={`Profile of ${review.userName || 'User'}`}
                                />
                                <div>
                                  <h4 className="font-medium">{review.userName}</h4>
                                  <div className="flex items-center">
                                    {renderStars(review.rating, false, 'sm')}
                                    <span className="text-xs text-gray-500 ml-2">{formatDate(review.date)}</span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleLikeReview(review.id)}
                                className={`flex items-center space-x-1 ${review.liked ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'
                                  } transition-colors`}
                              >
                                {review.liked ? <FaThumbsUp /> : <FaRegThumbsUp />}
                                <span className="text-xs">{review.likes}</span>
                              </button>
                            </div>
                            <p className="mt-2 text-gray-700">{review.comment}</p>
                          </div>
                        ))
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <p className="text-gray-500">No reviews yet</p>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSpecializationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full animate-fade-in">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-dark mb-4 font-display">Request Specialization Change</h3>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Specialization:</label>
                <div className="text-lg font-medium text-gray-800">{trainer.service}</div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Specialization:</label>
                <select
                  value={newSpecialization}
                  onChange={(e) => setNewSpecialization(e.target.value)}
                  className="w-full p-3 border-2 border-green-300 rounded-lg focus:border-green-500 focus:outline-none"
                >
                  {AVAILABLE_SERVICES.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-2">Note: Changes require admin approval</p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSpecializationModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSpecializationSubmit}
                  disabled={!newSpecialization || newSpecialization === trainer.service}
                  className={`px-4 py-2 rounded-lg transition-colors ${!newSpecialization || newSpecialization === trainer.service
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal for Multi-Service */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          // After viewing upgrade info, allow proceeding with specialization change
          // Note: Currently this is informational. When multi-service is fully implemented,
          // the upgrade prompt will block adding a second service unless upgraded
          setShowSpecializationModal(true);
          setNewSpecialization(trainer.service);
        }}
        reason={upgradeReason}
      />
    </div>
  );
}

export default TrainerProfile;